import { useAuth } from "@repo/auth";
import { Button } from "@repo/components";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";

/**
 * Post-logout landing — the page Keycloak returns to after `end_session` (see
 * `post_logout_redirect_uri` in @repo/auth config). It is PUBLIC and, crucially, does NOT sit behind
 * `RequireAuth` and does NOT auto-call `login()`. That is the whole point: because the realm's browser
 * flow now forwards straight to Google, landing logout back on a protected route (`/`) would fire
 * `login()` → Google → and Google's still-live SSO session would silently sign the user straight back
 * in — so "Se déconnecter" looked like a no-op. Ending here breaks that loop: the app session is gone
 * and the user stays signed out until they explicitly choose to sign in again.
 *
 * Signing back in is a normal (silent, if Google is still logged in) SSO round trip — that is expected
 * and fine; what was broken was doing it *automatically* with no logged-out state in between.
 */
export function LoggedOut() {
  const { login } = useAuth();
  const { t } = useTranslation();

  return (
    <main
      className={css({
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minH: "100dvh",
        bg: "bg.canvas",
        px: "4",
        py: "8",
      })}
    >
      <div
        className={css({
          w: "full",
          maxW: "sm",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "6",
          bg: "bg.default",
          borderWidth: "1px",
          borderColor: "border.default",
          rounded: "xl",
          boxShadow: "lg",
          px: { base: "6", sm: "8" },
          py: "10",
          textAlign: "center",
        })}
      >
        <h1 className={css({ fontSize: "2xl", fontWeight: "bold", color: "fg.default" })}>
          {t("loggedOut.title")}
        </h1>
        <p className={css({ color: "fg.muted", fontSize: "sm" })}>{t("loggedOut.subtitle")}</p>
        <Button
          size="lg"
          className={css({ w: "full" })}
          onClick={() => {
            login();
          }}
        >
          {t("loggedOut.cta.signIn")}
        </Button>
      </div>
    </main>
  );
}
